'use client'

import React, { memo } from 'react'
import { Button } from '../ui/button'
import ToggleSwitch from '../ui/toggle-switch'
import SelectField from '../ui/select-field'
import { SettingsState } from '@/types/settings'

interface VisualizationSettingsPanelProps {
  settings: SettingsState
  onSettingChange: (key: keyof SettingsState, value: any) => void
  onResetSection: (section: string) => void
}

const VisualizationSettingsPanel = memo<VisualizationSettingsPanelProps>(({
  settings,
  onSettingChange,
  onResetSection
}) => {
  return (
    <div className="space-y-6">
      {/* PERFORMANCE & RESET BAR */}
      <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
        <div className="flex items-center space-x-4 text-xs">
          <span className="text-slate-400">Particles:</span>
          <span className="text-purple-400 font-mono">{settings.particleCount.toLocaleString()}</span>
          <span className="text-slate-400">Frame Rate:</span>
          <span className="text-green-400 font-mono">{settings.frameRate}fps</span>
        </div>
        <Button
          onClick={() => onResetSection('visualization')}
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
        >
          Reset
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Theme - VISUAL POWER */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="theme">
            Theme <span className="text-cyan-400">{settings.theme}</span>
          </label>
          <SelectField
            id="theme"
            value={settings.theme}
            onChange={(value) => onSettingChange('theme', value)}
            options={[
              { value: 'dark', label: 'Dark (Professional)' },
              { value: 'cosmic', label: 'Cosmic (Space Theme)' },
              { value: 'neon', label: 'Neon (Cyberpunk)' },
              { value: 'minimal', label: 'Minimal (Clean)' },
              { value: 'matrix', label: 'Matrix (Terminal)' }
            ]}
            ariaLabel="Select visual theme"
          />
        </div>

        {/* Color Scheme - PALETTE POWER */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-200" htmlFor="colorScheme">
            Color Scheme <span className="text-purple-400">{settings.colorScheme}</span>
          </label>
          <SelectField
            id="colorScheme"
            value={settings.colorScheme}
            onChange={(value) => onSettingChange('colorScheme', value)}
            options={[
              { value: 'ocean', label: 'Ocean (Blue/Cyan)' },
              { value: 'sunset', label: 'Sunset (Orange/Purple)' },
              { value: 'forest', label: 'Forest (Green)' },
              { value: 'fire', label: 'Fire (Red/Yellow)' },
              { value: 'electric', label: 'Electric (Blue/Purple)' },
              { value: 'plasma', label: 'Plasma (Purple/Pink)' }
            ]}
            ariaLabel="Select color scheme"
          />
        </div>
      </div>

      {/* Particle Count - VISUAL INTENSITY */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="particleCount">
          Particle Count: <span className="text-orange-400">{settings.particleCount.toLocaleString()}</span>
        </label>
        <input
          id="particleCount"
          type="range"
          min="100"
          max="10000"
          step="100"
          value={settings.particleCount}
          onChange={(e) => onSettingChange('particleCount', parseInt(e.target.value))}
          className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
          aria-label="Adjust particle count for visualizations"
        />
        <div className="flex justify-between text-xs text-slate-400">
          <span>Minimal (100)</span>
          <span>Maximum (10,000)</span>
        </div>
      </div>

      {/* Frame Rate - SMOOTHNESS CONTROL */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-200" htmlFor="frameRate">
          Frame Rate: <span className="text-green-400">{settings.frameRate}fps</span>
        </label>
        <input
          id="frameRate"
          type="range"
          min="30"
          max="120"
          step="10"
          value={settings.frameRate}
          onChange={(e) => onSettingChange('frameRate', parseInt(e.target.value))}
          className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
          aria-label="Adjust frame rate for smooth animations"
        />
        <div className="flex justify-between text-xs text-slate-400">
          <span>30fps (Smooth)</span>
          <span>120fps (Ultra Smooth)</span>
        </div>
      </div>

      {/* POWER TOGGLES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Glow Effects Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Glow Effects</h4>
            <p className="text-xs text-slate-400">Visual effects</p>
          </div>
          <ToggleSwitch
            value={settings.enableGlowEffects}
            onChange={(value) => onSettingChange('enableGlowEffects', value)}
            activeColor="cyan"
            title="Toggle glow effects"
            ariaLabel={(value) => value ? "Disable glow effects" : "Enable glow effects"}
          />
        </div>

        {/* Particles Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Particles</h4>
            <p className="text-xs text-slate-400">Particle system</p>
          </div>
          <ToggleSwitch
            value={settings.enableParticles}
            onChange={(value) => onSettingChange('enableParticles', value)}
            activeColor="purple"
            title="Toggle particle system"
            ariaLabel={(value) => value ? "Disable particles" : "Enable particles"}
          />
        </div>

        {/* Shaders Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-600/30">
          <div>
            <h4 className="text-sm font-medium text-slate-200">Shaders</h4>
            <p className="text-xs text-slate-400">GPU effects</p>
          </div>
          <ToggleSwitch
            value={settings.enableShaders}
            onChange={(value) => onSettingChange('enableShaders', value)}
            activeColor="green"
            title="Toggle shader effects"
            ariaLabel={(value) => value ? "Disable shaders" : "Enable shaders"}
          />
        </div>
      </div>
    </div>
  )
})

VisualizationSettingsPanel.displayName = 'VisualizationSettingsPanel'

export default VisualizationSettingsPanel